// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import React, { useEffect, useRef, useState } from 'react';
import { VolumeContext } from './volume-context';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import { API_BASE } from '@/config.ts';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';

type VolumeInfo = {
  volume: number;
  muted: boolean;
  position?: { x: number; y: number; z: number };
};

type PositionInfo = {
  channelId: string;
  position?: { x: number; y: number; z: number };
}

// MUST BE USED WITH <CallProvider> and inside <AuthProvider> and <ExternalSocketProvider/>
export const VolumeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [volumes, setVolumes] = useState<Record<string, number>>({});
  const [mutedParticipants, setMutedParticipants] = useState<string[]>([]);
  const { user } = useAuth();
  const pendingUpdates = useRef<Record<string, VolumeInfo>>({});
  const pendingPositionUpdates = useRef<Record<string, PositionInfo>>({});
  const batchSaveTimer = useRef<NodeJS.Timeout | null>(null);
  const batchSendTimer = useRef<NodeJS.Timeout | null>(null);

  const [positions, setPositions] = useState<Record<string, { x: number; y: number; z: number }>>({});
  const socket = useSocket();

  useEffect(() => {
    if (!user?.id) return;

    fetch(`${API_BASE}/api/participant-volume/${user.id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
      .then(res => res.json())
      .then((data: { participantId: string; volume: number; muted: boolean }[]) => {
        const vol: Record<string, number> = {};
        const muted: string[] = [];
        data.forEach(p => {
          vol[p.participantId] = p.volume;
          if (p.muted) muted.push(p.participantId);
        });
        setVolumes(vol);
        setMutedParticipants(muted);
      })
      .catch(console.error);
  }, [user?.id]);

  const scheduleBatchSave = () => {
    if (batchSaveTimer.current) clearTimeout(batchSaveTimer.current);

    batchSaveTimer.current = setTimeout(() => {
      const updates = { ...pendingUpdates.current };
      pendingUpdates.current = {};

      // Send one request per participant (or optionally, extend backend to accept multiple updates at once)
      Object.entries(updates).forEach(([participantId, info]) => {
        fetch(`${API_BASE}/api/participant-volume/${user?.id}/${participantId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(info),
        }).catch(console.error);
      });

      batchSaveTimer.current = null;
    }, 300); // batch 300ms
  };

  const schedulePositionBatchSave = () => {
    if (batchSaveTimer.current) clearTimeout(batchSaveTimer.current);

    batchSaveTimer.current = setTimeout(() => {
      const updates = { ...pendingPositionUpdates.current };
      pendingPositionUpdates.current = {};

      Object.entries(updates).forEach(([participantId, info]) => {
        fetch(`${API_BASE}/api/participant/position/${info.channelId}/${participantId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ position: info.position }),
        }).catch(console.error);
      });

      batchSaveTimer.current = null;
    }, 300);
  };

  /**
   *   @experimental
    */
  const schedulePositionBatchSend = () => {
    if (batchSendTimer.current) clearTimeout(batchSendTimer.current);

    batchSendTimer.current = setTimeout(() => {
      const updates = { ...pendingPositionUpdates.current };
      pendingPositionUpdates.current = {};

      if (socket && socket.readyState === WebSocket.OPEN) {
        Object.entries(updates).forEach(([userId, info]) => {
          socket.send(JSON.stringify({
            type: "update-position",
            channelId: info.channelId,
            position: info.position,
            participantIdentity: userId, // временно, до рефакторинга
          }));
        });
      }

      batchSendTimer.current = null;
    }, 120);
  };

  const setVolume = (participantId: string, volume: number) => {
    setVolumes((prev) => ({
      ...prev,
      [participantId]: volume,
    }));
    pendingUpdates.current[participantId] = {
      volume,
      muted: mutedParticipants.includes(participantId),
    };
    scheduleBatchSave();
  };

  const getVolume = (participantId: string) => volumes[participantId] ?? 1;

  const muteParticipant = (participantId: string) => {
    setMutedParticipants((prev) => [...new Set([...prev, participantId])]);
    pendingUpdates.current[participantId] = {
      volume: volumes[participantId] ?? 1,
      muted: true,
    };
    scheduleBatchSave();
  };

  const unmuteParticipant = (participantId: string) => {
    setMutedParticipants((prev) => prev.filter((id) => id !== participantId));
    pendingUpdates.current[participantId] = {
      volume: volumes[participantId] ?? 1,
      muted: false,
    };
    scheduleBatchSave();
  };

  const isMuted = (participantId: string) =>
    mutedParticipants.includes(participantId);

  const updatePosition = (participantId: string, position: { x: number; y: number; z: number }, channelId: string) => {
    setPositions(prev => ({ ...prev, [participantId]: position }));

    pendingPositionUpdates.current[participantId] = { position, channelId };
    schedulePositionBatchSend();
    // schedulePositionBatchSave();

    fetch(`${API_BASE}/api/participant/position/${channelId}/${participantId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ position: position }),
    }).catch(console.error);
  };

  // TODO: load positions from DB?

  useEffect(() => {
    if (!socket) return;

    const handleMessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      if (data.type === "position-update") {
        const { userId, position } = data;

        if (position) {
          setPositions(prev => ({
            ...prev,
            [userId]: position,
          }));
        }
      }
    };

    socket.addEventListener("message", handleMessage);
    return () => socket.removeEventListener("message", handleMessage);
  }, [socket]);

  return (
    <VolumeContext.Provider
      value={{
        volumes,
        setVolume,
        getVolume,
        mutedParticipants,
        muteParticipant,
        unmuteParticipant,
        isMuted,
        positions,
        setPosition: updatePosition,
      }}
    >
      {children}
    </VolumeContext.Provider>
  );
};
